import { useState, useEffect, useRef, useCallback } from 'react';
import NotificationService from '../services/NotificationService';
import type { Notification } from '../types/notifications';

interface UseNotificationPollingProps {
  enabled?: boolean;
  intervalMs?: number;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  totalCount: number;
  isLoading: boolean;
  error: string | null;
  lastChecked: Date | null;
}

export const useNotificationPolling = ({ 
  enabled = true, 
  intervalMs = 5000 
}: UseNotificationPollingProps = {}) => {
  const [state, setState] = useState<NotificationState>({
    notifications: [],
    unreadCount: 0,
    totalCount: 0,
    isLoading: false,
    error: null,
    lastChecked: null
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const fetchingRef = useRef(false);

  const fetchNotifications = useCallback(async (showLoading = true) => {
    // Prevent concurrent calls
    if (fetchingRef.current) {
      console.log('⚠️ fetchNotifications already running, skipping');
      return;
    }

    if (!mountedRef.current) {
      console.log('⚠️ fetchNotifications called but component is unmounted');
      return;
    }

    console.log('🔄 fetchNotifications called with showLoading:', showLoading);
    fetchingRef.current = true;

    try {
      if (showLoading && mountedRef.current) {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
      }

      // Get all notifications (first page)
      console.log('📞 Calling NotificationService.getAllNotifications...');
      const allNotifications = await NotificationService.getAllNotifications(1, 20);
      console.log('📧 Polling hook fetched notifications:', {
        count: allNotifications.notifications.length,
        totalCount: allNotifications.totalCount,
        showLoading
      });
      
      if (!mountedRef.current) {
        console.log('⚠️ Component unmounted after getAllNotifications');
        return;
      }

      // Get unread count separately to avoid blocking if this fails
      let unreadCount = 0;
      try {
        console.log('📞 Calling NotificationService.getUnreadNotifications...');
        const unreadNotifications = await NotificationService.getUnreadNotifications();
        unreadCount = unreadNotifications.length;
        console.log('🔔 Unread count:', unreadCount);
      } catch (unreadError) {
        console.warn('Failed to fetch unread count:', unreadError);
        // Calculate unread count from notifications we have
        unreadCount = allNotifications.notifications.filter(n => !n.isRead).length;
        console.log('🔔 Calculated unread count from notifications:', unreadCount);
      }

      if (!mountedRef.current) {
        console.log('⚠️ Component unmounted after getUnreadNotifications');
        return;
      }

      console.log('✅ Setting state with notifications data');
      setState(prev => ({
        ...prev,
        notifications: allNotifications.notifications,
        totalCount: allNotifications.totalCount,
        unreadCount: unreadCount,
        isLoading: false,
        error: null,
        lastChecked: new Date()
      }));

    } catch (error: any) {
      if (!mountedRef.current) {
        console.log('⚠️ Component unmounted during error handling');
        return;
      }
      
      console.error('❌ Notification polling error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to fetch notifications'
      }));
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  const refreshNotifications = useCallback(() => {
    console.log('🔄 Manual refresh requested');
    fetchNotifications(true);
  }, [fetchNotifications]);

    // Start/stop polling based on enabled prop
  useEffect(() => {
    console.log('🔄 Polling effect triggered:', { enabled, intervalMs });
    
    // Always clear existing interval first
    if (intervalRef.current) {
      console.log('🧹 Clearing existing interval');
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (enabled && mountedRef.current) {
      console.log('🚀 Starting polling...');
      
      // Initial fetch
      fetchNotifications(true);

      // Set up polling interval
      intervalRef.current = setInterval(() => {
        if (mountedRef.current) {
          fetchNotifications(false); // Don't show loading for background polls
        }
      }, intervalMs);
      
      console.log('⏰ Polling interval set with', intervalMs, 'ms');
    }

    return () => {
      console.log('🧹 Cleaning up polling interval');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, intervalMs, fetchNotifications]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    console.log('🏗️ Component mounted, polling hook initialized');
    
    return () => {
      console.log('🔚 Component unmounting, cleaning up polling hook');
      mountedRef.current = false;
      fetchingRef.current = false;
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return {
    ...state,
    refreshNotifications
  };
}; 